import { useId, useMemo } from "react";
import {
	Bar,
	CartesianGrid,
	Cell,
	ComposedChart,
	Line,
	LabelList,
	Pie,
	PieChart,
	Scatter,
	ScatterChart,
	XAxis,
	YAxis,
} from "recharts";

import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
	type ChartConfig,
} from "./chart";
import { HeatmapChart } from "./heatmapChart";

export interface DataChartSeries {
	key: string;
	label: string;
	color?: string;
	kind?: "bar" | "line";
	axis?: "right";
	stack?: string;
	barLabel?: string;
}
export interface DataChartModel {
	kind: "pie" | "donut" | "cartesian" | "scatter" | "heatmap";
	heatmap?: { columns: string[]; rows: string[] };
	data: Record<string, string | number | null>[];
	series: DataChartSeries[];
	horizontal?: boolean;
	percent?: boolean;
	xLabel?: string;
	yLabel?: string;
	rightLabel?: string;
	showLegend?: boolean;
	categoryTotals?: boolean;
	barGradient?: boolean;
	categoryWindow?: number;
}

export const chartPalette = ["#6366f1", "#14b8a6", "#f59e0b", "#38bdf8", "#a78bfa", "#f43f5e", "#84cc16", "#f97316"];
const numberLabel = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 2 });
const tickLabel = (value: number) =>
	Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(value);

function tint(color: string, amount: number) {
	if (!/^#[\da-f]{6}$/i.test(color)) return color;
	return (
		"#" +
		[1, 3, 5]
			.map((start) =>
				Math.round(parseInt(color.slice(start, start + 2), 16) * (1 - amount) + 255 * amount)
					.toString(16)
					.padStart(2, "0"),
			)
			.join("")
	);
}
function labelColor(color?: string) {
	if (!color || !/^#[\da-f]{6}$/i.test(color)) return "white";
	const rgb = [1, 3, 5].map((start) => parseInt(color.slice(start, start + 2), 16));
	return (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000 >= 180 ? "black" : "white";
}
function CategoryTick({ x, y, value, total }: { x: number; y: number; value: string; total: number }) {
	const lines: string[] = [];
	for (const word of value.split(/\s+/)) {
		if (!lines.length || (lines[lines.length - 1] + " " + word).length > 20) lines.push(word);
		else lines[lines.length - 1] += " " + word;
	}
	return (
		<text x={x} y={y + 14} textAnchor="middle" fill="currentColor" fontSize={11}>
			<title>
				{value}: {numberLabel(total)}
			</title>
			{lines.slice(0, 3).map((line, index) => (
				<tspan key={index} x={x} dy={index ? 14 : 0}>
					{line}
				</tspan>
			))}
			<tspan x={x} dy={16}>
				{numberLabel(total)}
			</tspan>
		</text>
	);
}

/** Reusable shadcn charts. Callers supply normalized data; no report-specific parsing lives here. */
export function DataChart({ model, title }: { model: DataChartModel; title: string }) {
	const gradientId = useId().replace(/:/g, "");
	const { data, series, kind, horizontal, percent } = model;
	const plottedData = useMemo(
		() =>
			model.categoryWindow && data.length < model.categoryWindow
				? [...data, ...Array.from({ length: model.categoryWindow - data.length }, () => ({ category: "" }))]
				: data,
		[data, model.categoryWindow],
	);
	const circular = kind === "pie" || kind === "donut";
	const config = useMemo(() => {
		const result: ChartConfig = {};
		series.forEach((item, index) => {
			result[item.key] = { label: item.label, color: item.color ?? chartPalette[index % chartPalette.length] };
		});
		if (circular)
			data.forEach((row, index) => {
				result[`slice${index}`] = {
					label: String(row.category),
					color: String(row.fill || chartPalette[index % chartPalette.length]),
				};
			});
		return result;
	}, [series, data, circular]);
	const color = (key: string) => `var(--color-${key})`;
	const legend = model.showLegend !== false && (
		<ChartLegend
			content={(props) => (
				<ChartLegendContent
					verticalAlign={props.verticalAlign}
					className="flex-wrap gap-x-4 gap-y-1"
					nameKey={circular ? "slice" : kind === "scatter" ? "value" : undefined}
					payload={
						circular
							? props.payload?.filter(
									(item, index, all) =>
										all.findIndex((other) => other.value === item.value) === index,
								)
							: props.payload
					}
				/>
			)}
		/>
	);
	const tooltip = (
		<ChartTooltip
			content={
				<ChartTooltipContent
					labelFormatter={(label, payload) =>
						payload[0]?.payload?.tooltipLabel ?? payload[0]?.payload?.category ?? label
					}
				/>
			}
		/>
	);
	const right = series.some((item) => item.axis === "right");
	const numericAxis = {
		tickLine: false,
		axisLine: false,
		tickFormatter: percent ? (value: number) => `${Math.round(value * 100)}%` : tickLabel,
	};
	if (kind === "heatmap" && model.heatmap) return <HeatmapChart model={model} title={title} />;
	return (
		// Provide a default height in document flow, but allow flex layouts to grow or shrink the chart.
		<div
			className="w-full min-w-0"
			style={{ flex: "1 1 auto", height: 240, minHeight: 0 }}
			role="figure"
			aria-label={title}
		>
			<div className="h-full w-full overflow-x-auto">
				<ChartContainer
					config={config}
					className="aspect-auto h-full w-full"
					style={{
						minHeight: 0,
						width: model.categoryWindow
							? `${Math.max(1, data.length / model.categoryWindow) * 100}%`
							: "100%",
					}}
				>
					{circular ? (
						<PieChart accessibilityLayer>
							{series.map((item, index) => (
								<Pie
									key={item.key}
									data={data.map((row, i) => ({ ...row, slice: `slice${i}` }))}
									dataKey={item.key}
									nameKey="slice"
									innerRadius={kind === "pie" ? 0 : `${25 + index * (55 / series.length)}%`}
									outerRadius={`${25 + (index + 1) * (55 / series.length)}%`}
									isAnimationActive={false}
								>
									{data.map((_, i) => (
										<Cell key={i} fill={color(`slice${i}`)} />
									))}
								</Pie>
							))}
							<ChartTooltip
								content={
									<ChartTooltipContent
										hideLabel
										formatter={(value, name, item) => {
											const metric = series.find((s) => s.key === item.dataKey);
											const total = data.reduce(
												(sum, row) => sum + Number(row[metric?.key ?? ""] ?? 0),
												0,
											);
											return (
												<span>
													{config[String(name)]?.label}: {numberLabel(Number(value))} (
													{total ? numberLabel((Number(value) / total) * 100) : 0}%)
													{series.length > 1 && <span> · {metric?.label}</span>}
												</span>
											);
										}}
									/>
								}
							/>
							{legend}
						</PieChart>
					) : kind === "scatter" ? (
						<ScatterChart accessibilityLayer margin={{ top: 15, right: 20, bottom: 20, left: 5 }}>
							<CartesianGrid vertical={false} />
							<XAxis
								dataKey="x"
								type="number"
								name={model.xLabel}
								{...numericAxis}
								label={{ value: model.xLabel, position: "insideBottom", offset: -15 }}
							/>
							<YAxis dataKey="y" type="number" name={model.yLabel} {...numericAxis} />
							<ChartTooltip
								content={
									<ChartTooltipContent
										labelFormatter={(_, payload) => payload[0]?.payload?.category}
									/>
								}
							/>
							{legend}
							{series.map((item) => (
								<Scatter
									key={item.key}
									name={item.key}
									data={data.filter((row) => row.group === item.key)}
									fill={color(item.key)}
									isAnimationActive={false}
								/>
							))}
						</ScatterChart>
					) : (
						<ComposedChart
							data={plottedData}
							layout={horizontal ? "vertical" : "horizontal"}
							stackOffset={percent ? "expand" : "none"}
							accessibilityLayer
							barCategoryGap={model.categoryTotals ? "21%" : undefined}
							margin={{ top: 15, right: 15, bottom: 5, left: 5 }}
						>
							{model.barGradient && (
								<defs>
									{series.map((item) => (
										<linearGradient
											key={item.key}
											id={`${gradientId}-${item.key}`}
											x1="0"
											y1="0"
											x2="1"
											y2="0"
										>
											<stop offset="0%" stopColor={item.color} />
											<stop offset="22%" stopColor={tint(item.color ?? "#6366f1", 0.25)} />
											<stop offset="100%" stopColor={item.color} />
										</linearGradient>
									))}
								</defs>
							)}
							<CartesianGrid vertical={model.categoryTotals ?? false} />
							<XAxis
								dataKey={horizontal ? undefined : "category"}
								type={horizontal ? "number" : "category"}
								tickLine={false}
								axisLine={false}
								{...(horizontal ? numericAxis : { minTickGap: 20 })}
								{...(model.categoryTotals
									? {
											height: 78,
											interval: 0,
											tick: (props) =>
												props.index >= data.length ? (
													<g />
												) : (
													<CategoryTick
														x={Number(props.x)}
														y={Number(props.y)}
														value={String(props.payload.value)}
														total={Number(data[props.index]?.categoryTotal ?? 0)}
													/>
												),
										}
									: {})}
							/>
							<YAxis
								yAxisId="left"
								dataKey={horizontal ? "category" : undefined}
								type={horizontal ? "category" : "number"}
								width={horizontal ? 100 : 55}
								{...(horizontal ? { tickLine: false, axisLine: false } : numericAxis)}
								allowDecimals={!model.categoryTotals}
								tickCount={model.categoryTotals ? 7 : undefined}
							/>
							{right && (
								<YAxis
									yAxisId="right"
									orientation="right"
									{...numericAxis}
									label={{ value: model.rightLabel, angle: 90, position: "insideRight" }}
								/>
							)}
							{tooltip}
							{legend}
							{series.map((item) =>
								item.kind === "line" ? (
									<Line
										key={item.key}
										dataKey={item.key}
										name={item.label}
										yAxisId={item.axis ?? "left"}
										stroke={color(item.key)}
										strokeWidth={2}
										dot={false}
										isAnimationActive={false}
									/>
								) : (
									<Bar
										key={item.key}
										dataKey={item.key}
										name={item.label}
										yAxisId={item.axis ?? "left"}
										stackId={item.stack}
										fill={model.barGradient ? `url(#${gradientId}-${item.key})` : color(item.key)}
										isAnimationActive={false}
									>
										{!model.barGradient &&
											series.length === 1 &&
											data.map((row, i) => (
												<Cell key={i} fill={String(row.fill || color(item.key))} />
											))}
										{item.barLabel !== undefined && (
											<LabelList
												position="center"
												fill={labelColor(item.color)}
												fontSize={11}
												valueAccessor={(entry) => {
													const value = entry.value;
													return Array.isArray(value)
														? Number(value[1]) - Number(value[0])
														: Number(value ?? 0);
												}}
												formatter={(value) =>
													Number(value) > 0
														? `${item.barLabel} - ${numberLabel(Number(value))}`
														: ""
												}
											/>
										)}
									</Bar>
								),
							)}
						</ComposedChart>
					)}
				</ChartContainer>
			</div>
		</div>
	);
}
